import React from 'react'
import {
  SegmentedThermometer,
  Thermometer
} from '../../legacy-design-system/thermometer'
import { SeverityThermometerTooltip } from './severity-thermometer-tooltip'
import { EmptySeverityThermometerTooltip } from './empty-severity-thermometer-tooltip'
import {
  WithTooltipProps,
  WithTooltip,
  WithTooltipChildrenFnProps
} from '../../design-system'
import { css, cx } from '@emotion/css'
import { Vulnerability } from '../shared'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'

type Breakdown = SeverityBreakdown & VulnerabilityAggregations

type TooltipRendererFn = (props: {
  readonly severity: Severity
}) => React.ReactNode

interface Props {
  readonly breakdown: Breakdown
  readonly onSegmentClick?: (clickProps: {
    readonly severity: Severity
  }) => void
  readonly tooltipRenderer?: TooltipRendererFn
  readonly renderEmptyStateTooltip?: () => React.ReactNode
  readonly enableTooltips?: boolean
}

type Color =
  | 'red50'
  | 'orange50'
  | 'yellow50'
  | 'neutral50'
  | 'neutral40'
  | 'neutral30'

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'note'
type Status =
  | 'reported'
  | 'suspicious'
  | 'confirmed'
  | 'notAProblem'
  | 'remediated'
  | 'fixed'
  | 'autoRemediated'

interface VulnerabilityAggregations {
  readonly total: number
  readonly open: number
}
type StatusBreakdown = { readonly [key in Status]?: number }
export type StatusBreakdownWithTotal = StatusBreakdown &
  VulnerabilityAggregations

type SeverityBreakdown = {
  readonly [key in Severity]: StatusBreakdownWithTotal
}

export interface SeverityDataValue {
  total: number
  open: number
  vulnerabilities: Vulnerability[]
}
export interface SeverityData {
  critical: SeverityDataValue
  high: SeverityDataValue
  medium: SeverityDataValue
  low: SeverityDataValue
  note: SeverityDataValue
  total: number
  open: number
}

function getColorKey(severity: Severity): Color {
  switch (severity) {
    case 'critical':
      return 'red50'
    case 'high':
      return 'orange50'
    case 'medium':
      return 'yellow50'
    case 'low':
      return 'neutral50'
    case 'note':
      return 'neutral40'
    default:
      return 'neutral30'
  }
}

const clickableCss = css`
  cursor: pointer;
`

const keys: Severity[] = ['critical', 'high', 'medium', 'low', 'note']

interface ViewData {
  readonly severities: Severity[]
  readonly values: any[]
  readonly total: any
}

const buildViewData: (
  breakdown: Breakdown,
  key: 'open' | 'total'
) => ViewData = (breakdown, key: 'open' | 'total') => {
  const severities = keys.filter((k) => breakdown[k][key])
  const values = severities.map((k) => breakdown[k][key])
  const total = keys.reduce((acc, curr) => acc + breakdown[curr][key], 0)
  return { severities, values, total }
}

interface ExportedThermometerProps extends Props {
  readonly breakdown: Breakdown
}

export const OpenVulnerabilitiesSeverityThermometer = ({
  breakdown,
  ...props
}: ExportedThermometerProps) => (
  <SeverityThermometerView
    onlyOpen
    breakdown={breakdown}
    {...buildViewData(breakdown, 'open')}
    {...props}
  />
)

export const AllVulnerabilitiesSeverityThermometer = ({
  breakdown,
  ...props
}: ExportedThermometerProps) => (
  <SeverityThermometerView
    breakdown={breakdown}
    {...buildViewData(breakdown, 'total')}
    {...props}
  />
)

interface OptionalTooltipProps {
  readonly getRef?: WithTooltipChildrenFnProps['ref']
  readonly onMouseEnter?: WithTooltipChildrenFnProps['onMouseEnter']
  readonly onMouseLeave?: WithTooltipChildrenFnProps['onMouseLeave']
}

const EmptyStateThermometer = ({
  getRef,
  onMouseEnter,
  onMouseLeave
}: OptionalTooltipProps) => (
  <Thermometer
    fillRatio={100}
    fillColor="neutral30"
    getRef={getRef}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  />
)

function SeverityThermometerView({
  breakdown,
  tooltipRenderer,
  renderEmptyStateTooltip,
  enableTooltips,
  onSegmentClick,
  onlyOpen,
  severities,
  values,
  total
}: Props &
  ViewData & {
    readonly onlyOpen?: boolean
  }) {
  const sharedWithTooltipProps: {
    readonly placement: WithTooltipProps['placement']
  } = {
    placement: 'left'
  }

  /**
   * Empty state. Render a single thermometer.
   */
  if (total === 0) {
    return (
      <EmptyStateView
        {...{
          enableTooltips,
          sharedWithTooltipProps,
          renderEmptyStateTooltip
        }}
      />
    )
  }

  /**
   * Populated state. Render a segmented thermometer.
   */
  return (
    <SegmentedThermometer
      getColor={(i) => getColorKey(severities[i])}
      values={values}
      renderValue={({ index, width, propsGetter }) => {
        // This is an individual segment
        return (
          <SegmentValueView
            {...{
              severities,
              breakdown,
              onSegmentClick,
              sharedWithTooltipProps,
              enableTooltips,
              tooltipRenderer,
              onlyOpen,
              index,
              width,
              ...propsGetter()
            }}
          />
        )
      }}
    />
  )
}

const EmptyStateView = ({
  enableTooltips,
  sharedWithTooltipProps,
  renderEmptyStateTooltip
}) => {
  if (enableTooltips) {
    return (
      <WithTooltip
        {...sharedWithTooltipProps}
        width="100%"
        renderTooltipContent={() =>
          renderEmptyStateTooltip ? (
            renderEmptyStateTooltip()
          ) : (
            <EmptySeverityThermometerTooltip />
          )
        }
      >
        {({ ref, onMouseEnter, onMouseLeave }) => (
          <EmptyStateThermometer
            getRef={ref}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        )}
      </WithTooltip>
    )
  }

  return <EmptyStateThermometer />
}

const SegmentValueView = ({
  severities,
  breakdown,
  onSegmentClick,
  sharedWithTooltipProps,
  enableTooltips,
  tooltipRenderer,
  onlyOpen,
  index,
  width,
  className
}) => {
  const severity = severities[index]
  const statusBreakdown = breakdown[severity]

  const onClick = !!onSegmentClick
    ? () => onSegmentClick({ severity })
    : undefined

  const segmentCss = !!onSegmentClick ? cx(clickableCss, className) : className

  const sharedSegmentProps = {
    className: segmentCss,
    width,
    onClick
  }

  if (enableTooltips) {
    return (
      <Tooltip
        className={css`
          width: fit-content;
        `}
        {...sharedWithTooltipProps}
        body={
          tooltipRenderer ? (
            tooltipRenderer({ severity })
          ) : (
            <SeverityThermometerTooltip
              breakdown={statusBreakdown}
              severity={severity}
              onlyOpen={onlyOpen}
            />
          )
        }
      >
        <div
          data-e2e={
            !severity ? 'severity-value-none' : 'severity-value-' + severity
          }
          className={sharedSegmentProps.className}
          style={{ width }}
          onClick={sharedSegmentProps.onClick}
        />
      </Tooltip>
    )
  }

  return <Segment {...sharedSegmentProps} />
}

const Segment = ({
  dataE2e,
  className,
  width,
  onClick,
  getRef,
  onMouseEnter,
  onMouseLeave
}: {
  readonly dataE2e?: string
  readonly className?: string
  readonly width: string
  readonly onClick?: () => void
} & OptionalTooltipProps) => {
  return (
    <div
      data-e2e={dataE2e}
      className={className}
      style={{ width }}
      onClick={onClick}
      ref={getRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  )
}
